/**
 * Wishlist client.
 *
 * Deliberately dependency-free and small: it loads on every product page, and
 * the whole point of building this rather than installing an app is that it
 * does exactly what we need and nothing else.
 *
 * Identity is a random id kept in localStorage. No account, no email needed to
 * save something. An email is only asked for when someone wants telling that a
 * fish has come back, which is a separate, explicit opt-in.
 */
(function () {
  'use strict';

  var STORAGE_KEY = 'ra_wishlist_id';
  var CACHE_KEY = 'ra_wishlist_items';
  var EMAIL_KEY = 'ra_wishlist_has_email';

  /**
   * Which identity this shopper's list belongs to.
   *
   * Signed in, it is their account, so the list follows them between devices.
   * Signed out, it is a random id in this browser. The browser id is kept
   * either way, so a list built while logged out can be folded into the account
   * on the next sign-in rather than being lost.
   */
  function identity() {
    var el = document.querySelector('[data-customer-key]');
    var customerKey = el && el.dataset.customerKey;
    return customerKey && customerKey.length >= 16 ? customerKey : anonId();
  }

  function customerKey() {
    var el = document.querySelector('[data-customer-key]');
    var key = el && el.dataset.customerKey;
    return key && key.length >= 16 ? key : null;
  }

  /**
   * Fold a logged-out list into the account.
   *
   * Runs on every page load. A merge moves rows rather than copying them, so
   * once there is nothing under the browser id there is nothing to do, and the
   * call costs one cheap round trip. It used to remember it had run and refuse
   * to run again, which stranded anything saved under the browser id later.
   */
  function mergeIfNeeded(base) {
    var key = customerKey();
    if (!key) return Promise.resolve();

    var browserId = null;
    try {
      browserId = localStorage.getItem(STORAGE_KEY);
    } catch (e) {
      return Promise.resolve();
    }
    if (!browserId || browserId === key) return Promise.resolve();

    return fetch(base + '/api/merge', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ anonId: browserId, customerKey: key }),
    })
      .then(function (res) {
        if (!res.ok) throw new Error('HTTP ' + res.status);
      })
      .catch(function (err) {
        console.error('[wishlist] merge failed', err);
      });
  }

  /** Stable per-browser id. Matches the server's expected shape. */
  function anonId() {
    var id = null;
    try {
      id = localStorage.getItem(STORAGE_KEY);
    } catch (e) {
      // Private browsing with storage disabled: the wishlist simply won't persist.
      return null;
    }
    if (!id) {
      id = (crypto.randomUUID ? crypto.randomUUID() : String(Math.random()).slice(2) + Date.now())
        .replace(/-/g, '')
        .slice(0, 32);
      try {
        localStorage.setItem(STORAGE_KEY, id);
      } catch (e) {
        return null;
      }
    }
    return id;
  }

  /**
   * Locally cached set of saved variant ids, so buttons render in the right
   * state immediately rather than flashing "Save" then correcting themselves.
   */
  function cachedIds() {
    try {
      return new Set(JSON.parse(localStorage.getItem(CACHE_KEY) || '[]'));
    } catch (e) {
      return new Set();
    }
  }

  function setCachedIds(set) {
    try {
      localStorage.setItem(CACHE_KEY, JSON.stringify(Array.from(set)));
    } catch (e) {
      /* nothing we can do */
    }
  }

  /**
   * Whether we have somewhere to send an alert.
   *
   * A cached answer rather than a request on every product page. It is set by
   * the wishlist page, which knows for certain, and the worst case is a stale
   * flag that corrects itself next time that page loads. Being slightly behind
   * is cheaper than an API call on every product view.
   */
  function hasEmail() {
    try {
      return localStorage.getItem(EMAIL_KEY) === '1';
    } catch (e) {
      return false;
    }
  }

  function api(base, path, options) {
    return fetch(base + path, Object.assign({ headers: { 'Content-Type': 'application/json' } }, options));
  }

  /** The variant currently selected, read from the product form. */
  function currentVariantId(wrap) {
    var form = wrap.closest('form[action*="/cart/add"]') || document.querySelector('form[action*="/cart/add"]');
    var input = form && form.querySelector('[name="id"]');
    var fromForm = input && input.value;
    // Falls back to the server-rendered default before any variant switch.
    return String(fromForm || wrap.querySelector('[data-wishlist-toggle]').dataset.variantId || '');
  }

  function variantData(wrap, variantId) {
    var node = wrap.querySelector('[data-wishlist-variants]');
    if (!node) return null;
    var list;
    try {
      list = JSON.parse(node.textContent);
    } catch (e) {
      return null;
    }
    for (var i = 0; i < list.length; i++) {
      if (String(list[i].id) === String(variantId)) return list[i];
    }
    return null;
  }

  function render(wrap, saved) {
    var button = wrap.querySelector('[data-wishlist-toggle]');
    var label = wrap.querySelector('[data-wishlist-label]');
    var variantId = currentVariantId(wrap);
    var variant = variantData(wrap, variantId);
    var available = variant ? variant.available : true;

    button.classList.toggle('is-saved', saved);
    button.setAttribute('aria-pressed', saved ? 'true' : 'false');

    // Four states, because the button does two jobs. Out of stock, the label
    // states the benefit rather than the action - that is the wording that
    // earns the email address.
    if (saved) {
      // Only claim an email is coming when one actually can. Saying "we'll
      // email you" with no address on file is a promise we cannot keep, and
      // the customer walks away believing they are on a list.
      if (available) label.textContent = 'In your wishlist';
      else label.textContent = hasEmail() ? "We'll email you" : 'Saved';
    } else {
      label.textContent = available ? 'Add to wishlist' : "Email me when it's back";
    }
  }

  function setup(wrap) {
    var base = wrap.dataset.wishlistApi;
    var button = wrap.querySelector('[data-wishlist-toggle]');
    if (!base || !button) return;

    var id = identity();
    var saved = cachedIds();

    render(wrap, saved.has(currentVariantId(wrap)));

    // Warehouse swaps the variant by updating the form input; watching it keeps
    // the label honest without depending on a theme-specific event name.
    var form = wrap.closest('form[action*="/cart/add"]') || document.querySelector('form[action*="/cart/add"]');
    var input = form && form.querySelector('[name="id"]');
    if (input) {
      var last = input.value;
      setInterval(function () {
        if (input.value !== last) {
          last = input.value;
          render(wrap, cachedIds().has(String(last)));
        }
      }, 300);
    }

    button.addEventListener('click', function () {
      if (!id) {
        console.error('[wishlist] localStorage is blocked, so there is no identity to save against');
        var l = wrap.querySelector('[data-wishlist-label]');
        if (l) l.textContent = 'Unavailable in private mode';
        return;
      }

      var variantId = currentVariantId(wrap);
      var variant = variantData(wrap, variantId) || {};
      var ids = cachedIds();
      var isSaved = ids.has(variantId);

      button.disabled = true;

      var request = isSaved
        ? api(base, '/api/items', {
            method: 'DELETE',
            body: JSON.stringify({ anonId: id, variantId: variantId }),
          })
        : api(base, '/api/items', {
            method: 'POST',
            body: JSON.stringify({
              anonId: id,
              variantId: variantId,
              productId: button.dataset.productId,
              handle: button.dataset.handle,
              sku: variant.sku || null,
              title: variant.title || document.title,
              image: variant.image || null,
              // The button already declared the intent. "Email me when it's
              // back" on a sold-out fish is a request for an alert; "Add to
              // wishlist" on one sitting in stock is a bookmark. Defaulting
              // both to on meant bookmarking a fish demanded an email address
              // for a message nobody had asked for.
              notify: variant.available === false,
            }),
          });

      request
        .then(function (res) {
          if (res.ok && !isSaved) {
            // The server knows whether we can email this person; the local flag
            // is only a cache of that, and goes stale the moment they use a
            // second device. Take the server's word for it.
            res
              .clone()
              .json()
              .then(function (body) {
                try {
                  if (body.hasEmail) localStorage.setItem(EMAIL_KEY, '1');
                  else localStorage.removeItem(EMAIL_KEY);
                } catch (e) {
                  /* nothing we can do */
                }
                render(wrap, true);
                refreshLink();
              })
              .catch(function () {
                /* the save still counts */
              });
          }
          if (!res.ok) {
            // Surface what the server actually said, rather than a blank failure.
            return res.text().then(function (text) {
              throw new Error('HTTP ' + res.status + ' ' + text.slice(0, 200));
            });
          }
          if (isSaved) ids.delete(variantId);
          else ids.add(variantId);
          setCachedIds(ids);
          render(wrap, !isSaved);
          document.dispatchEvent(
            new CustomEvent('wishlist:changed', { detail: { count: ids.size, saved: !isSaved } })
          );
          // Only worth asking for an email once something is actually saved.
          if (!isSaved && !variant.available) askForEmail(base, id);
        })
        .catch(function (err) {
          // An alert() blocks the page and tells nobody anything useful. Report
          // it on the button, and put the real reason in the console where it
          // can be diagnosed.
          console.error('[wishlist] could not save', {
            error: (err && err.message) || String(err),
            variantId: variantId,
            api: base,
          });
          var label = wrap.querySelector('[data-wishlist-label]');
          if (label) {
            label.textContent = 'Please try again';
            setTimeout(function () {
              render(wrap, cachedIds().has(currentVariantId(wrap)));
            }, 2500);
          }
        })
        .finally(function () {
          button.disabled = false;
        });
    });
  }

  /**
   * Asked only after saving something that is out of stock, which is the moment
   * the offer makes sense. Declining leaves the item saved.
   */
  function askForEmail(base, id) {
    document.dispatchEvent(new CustomEvent('wishlist:wants-email', { detail: { anonId: id, api: base } }));
  }

  /**
   * The floating bubble, which is on every page rather than only product pages.
   *
   * Always shown; only the count waits for something to be saved, since a "0"
   * badge reads as broken rather than empty.
   */
  function refreshBubble() {
    var bubble = document.querySelector('[data-wishlist-bubble]');
    if (!bubble) return;
    var count = cachedIds().size;
    var badge = bubble.querySelector('[data-wishlist-bubble-count]');
    if (!badge) return;
    badge.textContent = String(count);
    badge.hidden = count === 0;
  }

  /** Keeps the "View your wishlist (n)" link in step, without moving anything. */
  function refreshLink() {
    var link = document.querySelector('[data-wishlist-link]');
    if (!link) return;

    var text = link.querySelector('[data-wishlist-link-text]');
    var count = cachedIds().size;

    // Having just saved something sold-out with no address on file, this line
    // stops being navigation and starts being the ask. Same link, same place,
    // no extra furniture — it just says the more useful thing.
    var wrap = document.querySelector('.wishlist-button-wrap');
    var needsEmail = false;
    if (wrap && !hasEmail()) {
      var variantId = currentVariantId(wrap);
      var variant = variantData(wrap, variantId);
      needsEmail = Boolean(variant) && variant.available === false && cachedIds().has(variantId);
    }

    if (text) {
      text.textContent = needsEmail
        ? 'Add your email to get notified'
        : 'View your wishlist (' + count + ')';
    }
    link.classList.toggle('is-asking', needsEmail);
    link.classList.toggle('is-visible', count > 0);
  }

  /**
   * Saving straight from a collection grid.
   *
   * Delegated from the document rather than bound per card, because grids
   * paginate, filter and load more, and every one of those replaces the cards
   * without warning.
   */
  function setupCardHearts(base) {
    document.addEventListener('click', function (e) {
      var heart = e.target.closest('[data-wishlist-card-toggle]');
      if (!heart) return;

      // The heart is a sibling of the card's link rather than inside it, so
      // this is belt and braces - but a card that navigated away mid-save
      // would lose the request.
      e.preventDefault();
      e.stopPropagation();

      var id = identity();
      if (!id) return;

      var variantId = String(heart.dataset.variantId || '');
      if (!variantId) return;

      var ids = cachedIds();
      var isSaved = ids.has(variantId);
      heart.disabled = true;

      var request = isSaved
        ? api(base, '/api/items', {
            method: 'DELETE',
            body: JSON.stringify({ anonId: id, variantId: variantId }),
          })
        : api(base, '/api/items', {
            method: 'POST',
            body: JSON.stringify({
              anonId: id,
              variantId: variantId,
              productId: heart.dataset.productId,
              handle: heart.dataset.handle,
              sku: heart.dataset.sku || null,
              title: heart.dataset.title,
              image: heart.dataset.image || null,
              // Same rule as the product page: asking to be told about a
              // sold-out fish is an alert request, saving one in stock is a
              // bookmark.
              notify: heart.dataset.available === 'false',
            }),
          });

      request
        .then(function (res) {
          if (!res.ok) throw new Error('HTTP ' + res.status);
          if (isSaved) ids.delete(variantId);
          else ids.add(variantId);
          setCachedIds(ids);
          syncCardHearts();
          document.dispatchEvent(
            new CustomEvent('wishlist:changed', { detail: { count: ids.size, saved: !isSaved } })
          );
        })
        .catch(function (err) {
          console.error('[wishlist] could not save from the grid', err);
        })
        .finally(function () {
          heart.disabled = false;
        });
    });
  }

  /** Marks every visible heart with what is actually saved. */
  function syncCardHearts() {
    var ids = cachedIds();
    document.querySelectorAll('[data-wishlist-card-toggle]').forEach(function (heart) {
      var saved = ids.has(String(heart.dataset.variantId || ''));
      heart.classList.toggle('is-saved', saved);
      heart.setAttribute('aria-pressed', saved ? 'true' : 'false');
      heart.title = saved ? 'In your wishlist' : 'Add to wishlist';
    });
  }

  function init() {
    // Deliberately not returning early when there is no product button: the
    // bubble is on every page, and the merge matters most on the wishlist page.
    var wraps = document.querySelectorAll('.wishlist-button-wrap');
    wraps.forEach(setup);
    refreshLink();
    refreshBubble();
    syncCardHearts();
    document.addEventListener('wishlist:changed', function () {
      refreshLink();
      refreshBubble();
      syncCardHearts();
    });

    // Any element carrying the API, not just a product button — the wishlist
    // page has no buttons, and that is exactly where a merge matters most.
    var host = document.querySelector('[data-wishlist-api]');
    var base = host && host.dataset.wishlistApi;
    if (base) {
      mergeIfNeeded(base);
      setupCardHearts(base);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Exposed so the wishlist page and the tab can reuse the same identity.
  window.RAWishlist = {
    anonId: anonId,
    identity: identity,
    customerKey: customerKey,
    cachedIds: cachedIds,
    setCachedIds: setCachedIds,
  };
})();
